import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ShieldCheck, LogOut, RefreshCw, Check, X, Pause, Play, Trash2 } from 'lucide-react';

// Control-plane konzole pro platformní super-adminy (mimo tenanty). Vlastní
// přihlášení (token v localStorage `ct-sa-token`) oddělené od lab aplikace.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'ct-sa-token';

const sa = axios.create({ baseURL: API });
sa.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

// Popisné labely veličin (modulů) pro zobrazení žádostí.
const QUANTITY_LABELS = {
  hmotnost: 'Hmotnost', teplota: 'Teplota', tlak: 'Tlak', vlhkost: 'Vlhkost',
  srazky: 'Srážky', proudeni: 'Proudění vzduchu', smer: 'Směr větru',
};
const qLabel = (k) => QUANTITY_LABELS[k] || k;

const STATUS_LABELS = {
  pending_email: { text: 'Čeká na e-mail', color: '#94a3b8' },
  pending_approval: { text: 'Čeká na schválení', color: '#d97706' },
  active: { text: 'Aktivní', color: '#16a34a' },
  suspended: { text: 'Pozastaveno', color: '#dc2626' },
  rejected: { text: 'Zamítnuto', color: '#64748b' },
};

export default function SuperAdminPage() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  useEffect(() => { document.title = 'Super-admin | CalibraTech 2.0'; }, []);
  return authed
    ? <Console onLogout={() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }} />
    : <LoginForm onLogin={() => setAuthed(true)} />;
}

function LoginForm({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await sa.post('/superadmin/login', { password });
      localStorage.setItem(TOKEN_KEY, data.accessToken);
      onLogin();
    } catch (err) {
      setError(err.response?.data?.detail || 'Přihlášení selhalo');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center" style={{ background: '#0f172a', padding: '2rem' }}>
      <div className="w-full" style={{ maxWidth: '24rem' }}>
        <div className="flex items-center justify-center gap-2.5 mb-6 text-white">
          <ShieldCheck className="w-7 h-7" style={{ color: '#60a5fa' }} />
          <span className="text-lg font-bold">CalibraTech — Super-admin</span>
        </div>
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '0.75rem', padding: '2rem' }}>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider" style={{ color: '#94a3b8' }}>Heslo</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-10" autoFocus />
            </div>
            {error && <div className="text-sm p-2 rounded" style={{ background: '#450a0a', color: '#fca5a5', border: '1px solid #7f1d1d' }}>{error}</div>}
            <Button type="submit" disabled={loading} className="w-full font-semibold" style={{ background: '#2563eb', color: '#fff', height: '2.75rem' }}>
              {loading ? 'Přihlašuji…' : 'Přihlásit'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Console({ onLogout }) {
  const [tenants, setTenants] = useState([]);
  const [moduleRequests, setModuleRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s, m] = await Promise.all([
        sa.get('/superadmin/tenants'),
        sa.get('/superadmin/stats'),
        sa.get('/superadmin/module-requests'),
      ]);
      setTenants(t.data); setStats(s.data); setModuleRequests(m.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); onLogout(); }
    } finally { setLoading(false); }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action, label, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(`${id}:${action}`);
    try {
      if (action === 'delete') await sa.delete(`/superadmin/tenants/${id}`);
      else await sa.post(`/superadmin/tenants/${id}/${action}`);
      await load();
    } catch (err) {
      window.alert(err.response?.data?.detail || `Akce „${label}" selhala`);
    } finally { setBusy(null); }
  };

  const actReq = async (id, action, label) => {
    setBusy(`req:${id}:${action}`);
    try {
      await sa.post(`/superadmin/module-requests/${id}/${action}`);
      await load();
    } catch (err) {
      window.alert(err.response?.data?.detail || `Akce „${label}" selhala`);
    } finally { setBusy(null); }
  };

  const enableModules = async (r) => {
    const id = r.id || r._id;
    const names = (r.requestedQuantities || []).map(qLabel).join(', ');
    if (!window.confirm(`Zapnout modul(y) „${names}" laboratoři „${r.tenantName}"?`)) return;
    setBusy(`req:${id}:enable`);
    try {
      await sa.post(`/superadmin/tenants/${r.tenantId}/modules/enable`, { quantities: r.requestedQuantities });
      // Po dodání modulu žádost rovnou schválíme (pokud ještě není vyřízená).
      if (r.status === 'pending') await sa.post(`/superadmin/module-requests/${id}/approve`);
      await load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Zapnutí modulu selhalo');
    } finally { setBusy(null); }
  };

  const fmtDate = (v) => v ? new Date(v).toLocaleDateString('cs-CZ') : '—';

  return (
    <div className="min-h-screen" style={{ background: '#0f172a', color: '#e2e8f0' }}>
      <header className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #1e293b' }}>
        <div className="flex items-center gap-2.5">
          <ShieldCheck className="w-6 h-6" style={{ color: '#60a5fa' }} />
          <span className="font-bold">CalibraTech — Super-admin konzole</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="ghost" className="text-slate-300" title="Obnovit"><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={() => { localStorage.removeItem(TOKEN_KEY); onLogout(); }} variant="ghost" className="text-slate-300 gap-1.5">
            <LogOut className="w-4 h-4" /> Odhlásit
          </Button>
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Stat label="Čeká na schválení" value={stats.pending} highlight={stats.pending > 0} />
            <Stat label="Žádosti o moduly" value={stats.pendingModuleRequests} highlight={stats.pendingModuleRequests > 0} />
            <Stat label="Aktivní" value={stats.active} />
            <Stat label="Pozastaveno" value={stats.suspended} />
            <Stat label="Celkem" value={stats.total} />
          </div>
        )}

        <h2 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#94a3b8' }}>Laboratoře</h2>
        {loading ? (
          <p style={{ color: '#64748b' }}>Načítám…</p>
        ) : tenants.length === 0 ? (
          <p style={{ color: '#64748b' }}>Zatím žádné registrace.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid #1e293b' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#1e293b', color: '#94a3b8' }}>
                  <Th>Laboratoř</Th><Th>E-mail admina</Th><Th>Stav</Th><Th>Registrace</Th><Th>Expirace</Th><Th>Akce</Th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => {
                  const st = STATUS_LABELS[t.status] || { text: t.status, color: '#94a3b8' };
                  const bk = (a) => busy === `${t.id || t._id}:${a}`;
                  const id = t.id || t._id;
                  return (
                    <tr key={id} style={{ borderTop: '1px solid #1e293b' }}>
                      <Td><div className="font-medium text-white">{t.name}</div><div className="text-xs" style={{ color: '#64748b' }}>{t.slug}</div></Td>
                      <Td>{t.adminEmail}</Td>
                      <Td><span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: `${st.color}22`, color: st.color }}>{st.text}</span></Td>
                      <Td>{fmtDate(t.createdAt)}</Td>
                      <Td>{fmtDate(t.expiresAt)}</Td>
                      <Td>
                        <div className="flex gap-1.5">
                          {t.status === 'pending_approval' && (
                            <>
                              <IconBtn title="Schválit" color="#16a34a" disabled={bk('approve')} onClick={() => act(id, 'approve', 'Schválit')}><Check className="w-4 h-4" /></IconBtn>
                              <IconBtn title="Zamítnout" color="#dc2626" disabled={bk('reject')} onClick={() => act(id, 'reject', 'Zamítnout', 'Opravdu zamítnout tuto registraci?')}><X className="w-4 h-4" /></IconBtn>
                            </>
                          )}
                          {t.status === 'active' && (
                            <IconBtn title="Pozastavit" color="#d97706" disabled={bk('suspend')} onClick={() => act(id, 'suspend', 'Pozastavit')}><Pause className="w-4 h-4" /></IconBtn>
                          )}
                          {t.status === 'suspended' && (
                            <IconBtn title="Aktivovat" color="#16a34a" disabled={bk('activate')} onClick={() => act(id, 'activate', 'Aktivovat')}><Play className="w-4 h-4" /></IconBtn>
                          )}
                          <IconBtn title="Smazat (vč. dat)" color="#dc2626" disabled={bk('delete')} onClick={() => act(id, 'delete', 'Smazat', `Smazat laboratoř „${t.name}" včetně VŠECH jejích dat? Nevratné.`)}><Trash2 className="w-4 h-4" /></IconBtn>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <h2 className="text-sm font-semibold uppercase tracking-wider mb-3 mt-8" style={{ color: '#94a3b8' }}>Žádosti o moduly</h2>
        {loading ? (
          <p style={{ color: '#64748b' }}>Načítám…</p>
        ) : moduleRequests.length === 0 ? (
          <p style={{ color: '#64748b' }}>Žádné žádosti o moduly.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid #1e293b' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#1e293b', color: '#94a3b8' }}>
                  <Th>Laboratoř</Th><Th>Veličiny</Th><Th>Poznámka</Th><Th>Stav</Th><Th>Vytvořeno</Th><Th>Akce</Th>
                </tr>
              </thead>
              <tbody>
                {moduleRequests.map((r) => {
                  const id = r.id || r._id;
                  const rk = (a) => busy === `req:${id}:${a}`;
                  const stColor = r.status === 'approved' ? '#16a34a' : r.status === 'rejected' ? '#64748b' : '#d97706';
                  const stText = r.status === 'approved' ? 'Schváleno' : r.status === 'rejected' ? 'Zamítnuto' : 'Čeká';
                  return (
                    <tr key={id} style={{ borderTop: '1px solid #1e293b' }}>
                      <Td><div className="font-medium text-white">{r.tenantName || '—'}</div></Td>
                      <Td>{(r.requestedQuantities || []).map(qLabel).join(', ')}</Td>
                      <Td><span className="text-xs" style={{ color: '#94a3b8' }}>{r.note || '—'}</span></Td>
                      <Td><span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: `${stColor}22`, color: stColor }}>{stText}</span></Td>
                      <Td>{fmtDate(r.createdAt)}</Td>
                      <Td>
                        <div className="flex gap-1.5">
                          <IconBtn title="Schválit a zapnout modul" color="#16a34a" disabled={rk('enable')} onClick={() => enableModules(r)}><Check className="w-4 h-4" /></IconBtn>
                          {r.status === 'pending' && (
                            <IconBtn title="Zamítnout" color="#dc2626" disabled={rk('reject')} onClick={() => actReq(id, 'reject', 'Zamítnout')}><X className="w-4 h-4" /></IconBtn>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="rounded-lg p-4" style={{ background: '#1e293b', border: `1px solid ${highlight ? '#d97706' : '#334155'}` }}>
      <div className="text-2xl font-bold text-white">{value ?? 0}</div>
      <div className="text-xs mt-1" style={{ color: '#94a3b8' }}>{label}</div>
    </div>
  );
}
const Th = ({ children }) => <th className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wider">{children}</th>;
const Td = ({ children }) => <td className="px-3 py-2 align-middle">{children}</td>;
function IconBtn({ title, color, onClick, disabled, children }) {
  return (
    <button type="button" title={title} onClick={onClick} disabled={disabled}
      className="p-1.5 rounded transition-opacity disabled:opacity-40"
      style={{ background: `${color}1a`, color }}>{children}</button>
  );
}
